import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState, type ReactElement, useMemo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { MdContentCopy } from 'react-icons/md'
import * as Yup from 'yup'

import type { USER_TYPE, IAppUser, IConnectionData } from '~/types/app-user'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { FORM_ERRORS } from '~/const'
import { useGetAccessTokenMutation } from '~/queries/app-users'
import { useApplicationsInfoListQuery } from '~/queries/applications-info'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'
import { YupOption } from '~/utils/yup'

import '../styles.scss'

interface Values {
  app: {
    label?: string
    value?: string
  }
  deviceLanguage: string
  installationId: string
}

const validationSchema = Yup.object().shape({
  app: YupOption.required(FORM_ERRORS.REQUIRED),
  deviceLanguage: Yup.string().required(),
  installationId: Yup.string().required(),
})

interface ModalFormProps {
  connectionData?: IConnectionData
  env: USER_TYPE
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: VoidFunction
  user: IAppUser
}

const GetTokenForm = ({ connectionData, env, onClose, onFailure, onSuccess, user }: ModalFormProps): ReactElement => {
  /** Queries & Mutations */

  const { data: apps, isLoading: isAppInfoLoading } = useApplicationsInfoListQuery('mwm')

  const { mutateAsync: generateAccessToken } = useGetAccessTokenMutation()

  /** Local state */

  const [accessToken, setAccessToken] = useState<string>()

  const [isTokenLoading, setIsTokenLoading] = useState(false)

  const options = useMemo(
    () => apps?.map(({ app_key, display_name }) => ({ label: display_name, value: app_key })) || [],
    [apps]
  )

  const defaultSelectValue = options?.find(({ value }: IOption) => connectionData?.appKey === value)

  const defaultFormValues = {
    app: { label: '', value: '' },
    deviceLanguage: connectionData?.deviceLanguage,
    installationId: connectionData?.installationId,
  }

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    setValue,
  } = useForm<Values>({ defaultValues: defaultFormValues, resolver: yupResolver(validationSchema) })

  const isApiLoading = isAppInfoLoading || isTokenLoading

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    setIsTokenLoading(true)

    if (connectionData) {
      const { app } = values

      if (app.value) {
        const match = apps?.find(({ app_key }) => app.value === app_key)

        if (match) {
          const { app_key } = match

          try {
            const { access_token } = await generateAccessToken({
              appKey: app_key,
              deviceLanguage: values.deviceLanguage,
              deviceType: connectionData.deviceType,
              env: env,
              installationId: values.installationId,
              userId: user.user_id,
            })
            setAccessToken(access_token)
            onSuccess()
          } catch (e) {
            onFailure('Error')
          }
        } else {
          onFailure('Error')
        }
      } else {
        displayToast('An app must be selected', 'info')
      }
    }

    setIsTokenLoading(false)
  }

  const handleAppSelectChange = (selectedOption: any) =>
    setValue('app', selectedOption, { shouldDirty: true, shouldTouch: true })

  const handleCopyToken = () => copyText(accessToken, 'The access token has been copied')

  const handleRegenerate = () => setAccessToken(undefined)

  /** Effects */

  useEffect(() => {
    if (defaultSelectValue) setValue('app', defaultSelectValue)
  }, [defaultSelectValue, setValue])

  return (
    <div className="token-container">
      {isApiLoading ? (
        <MwMLoader category="item" />
      ) : accessToken ? (
        <div className="token-wrapper">
          <Button className="copy-token-button" ghost onClick={handleCopyToken}>
            <MdContentCopy color="#626262" size={18} />
          </Button>
          {accessToken}
        </div>
      ) : (
        <form
          className="get-access-token-form modal-form"
          id="get-access-token-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <>
            <Controller
              control={control}
              name="app"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  defaultValue={defaultSelectValue}
                  error={errors.app?.message}
                  label="Application"
                  onChange={handleAppSelectChange}
                  options={options as IOption[]}
                />
              )}
            />
            <TextInput
              error={errors.installationId?.message}
              label="Installation ID"
              placeholder="Enter an installation ID"
              {...register('installationId')}
            />
            <TextInput
              error={errors.deviceLanguage?.message}
              label="Device language"
              placeholder="Enter a device language"
              {...register('deviceLanguage')}
            />
          </>
        </form>
      )}
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          disabled={!isValid}
          form={accessToken ? undefined : 'get-access-token-form'}
          onClick={() => accessToken && handleRegenerate()}
          type={accessToken ? 'button' : 'submit'}
        >
          {accessToken ? 'Re-generate' : 'Submit'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </div>
  )
}

interface ModalProps {
  connectionData?: IConnectionData
  env: USER_TYPE
  isOpen: boolean
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: VoidFunction
  user?: IAppUser
}

const GetTokenModal = ({
  connectionData,
  env,
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  user,
}: ModalProps): ReactElement => {
  return (
    <Modal className="get-access-token-modal apps-users-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Generate access token</ModalHeader>
      {user ? (
        <GetTokenForm
          connectionData={connectionData}
          env={env}
          onClose={onClose}
          onFailure={onFailure}
          onSuccess={onSuccess}
          user={user}
        />
      ) : null}
    </Modal>
  )
}

export default GetTokenModal
